import { useEffect, useState } from "react";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { CustomInput, CustomCheckbox } from "../Forms";
import { PrimaryButton, OutlineButton } from "../Buttons";
import PermissionInfoPopup from "./PermissionInfoPopup";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import { showSuccessToast, showErrorToast } from "@configs/react-toastify/snackbar";
import { CHARACTER_LENGTH } from "@constants/index";
import useEscapeKey from "@hooks/useEscapeKey";

const ModifyRoleModal = ({
  isOpen,
  onClose,
  mode = "create", // "create" or "edit"
  selectedRole = null, // For edit mode
  onSuccess, // Callback for successful operations
  "data-testid": dataTestId = "modify-role-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]); // Store original permissions for comparison
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: permissionList,
    isLoading: isCodesLoading,
    error: codesError,
    refetch: refetchPermissions,
  } = useFetchData(
    [queryKeys.getCodeListForRole],
    queryKeys.getCodeListForRole,
    {
      method: "GET",
    },
    {
      enabled: isOpen, // Only fetch when modal is open
    },
    {},
  );

  const updateRoleList = useMutationData(
    queryKeys.updateRoleList,
    {
      method: "PUT",
    },
    {
      onSuccess: (data) => {
        const isEdit = mode === "edit";
        showSuccessToast(isEdit ? t("role.update-success") : t("role.create-success"));
        setIsSubmitting(false);
        onSuccess?.(data);
        handleClose();
      },
      onError: (error) => {
        setIsSubmitting(false);
        if (error?.response && error?.response?.data) {
          const errorMsg = typeof error.response.data === "string" ? error.response.data : "";
          if (errorMsg.includes("duplicated")) {
            showErrorToast(t("role.name-duplicated"), "", "");
            return;
          }
          if (errorMsg === "This role is currently assigned to users who are managing one or more Products. Please reassign those Products before removing the Product Manager permission.") {
            showErrorToast(t("role.update-error"), t("role.update-error-product-manager-assignment"), "");
            return;
          }
          if (errorMsg === "This role is currently assigned to users who are managing one or more Projects. Please reassign those Projects before removing the Project Manager permission.") {
            showErrorToast(t("role.update-error"), t("role.update-error-project-manager-assignment"), "");
            return;
          }
          const isEdit = mode === "edit";
          showErrorToast(isEdit ? t("role.update-error") : t("role.create-error"));
        }
      },
    }
  );

  // Initialize form data when modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && selectedRole) {
        setRoleName(selectedRole.roleName || "");
        const permissions = selectedRole.permissions || [];
        setOriginalPermissions(permissions);
        setSelectedPermissions(permissions.map(p => p.permissionCd) || []);
      } else {
        setRoleName("");
        setSelectedPermissions([]);
        setOriginalPermissions([]);
      }
    }
  }, [isOpen, mode, selectedRole]);

  // Build permissionOptions from API response
  useEffect(() => {
    if (permissionList && Array.isArray(permissionList)) {
      const options = permissionList
        .filter(item => item.useFlag === "Y") // Only active permissions
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) // Sort by displayOrder
        .map(item => ({
          codeSeq: item.codeSeq,
          label: item.codeName,
          code: item.code
        }));

      setPermissionOptions(options);
    }
  }, [permissionList]);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePermissionChange = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };



  const handleSubmit = (e) => {
    e.preventDefault();
    if (roleName.trim() && !isSubmitting) {
      setIsSubmitting(true);
      
      // Build permissions array with db and delete flags
      const permissions = permissionOptions.map(permission => {
        const isSelected = selectedPermissions.includes(permission.code);
        const originalPermission = originalPermissions.find(p => p.permissionCd === permission.code);
        
        if (mode === "edit") {
          if (originalPermission) {
            // Permission existed in DB
            if (isSelected) {
              // Keep the permission (update)
              return {
                ...originalPermission,
                delete: false
              };
            } else {
              // Remove the permission (mark for deletion)
              return {
                ...originalPermission,
                delete: true
              };
            }
          } else {
            // Permission didn't exist in DB
            if (isSelected) {
              // Add new permission
              return {
                roleCd: selectedRole.code,
                roleCdName: selectedRole.code,
                permissionCd: permission.code,
                permissionCdName: permission.label,
                db: false,
                delete: false
              };
            }
            // Skip if not selected and didn't exist
            return null;
          }
        } else {
          // Create mode - all selected permissions are new
          if (isSelected) {
            return {
              permissionCd: permission.code,
              permissionCdName: permission.label,
              roleCd: roleName.trim(),
              db: false,
              delete: false
            };
          }
          return null;
        }
      }).filter(Boolean); // Remove null entries

      const roleData = mode === "edit" && selectedRole ? {
        ...selectedRole,
        code: roleName.trim(),
        codeName: roleName.trim(),
        permissions: permissions,
        mode: mode
      } : {
        code: roleName.trim(),
        codeName: roleName.trim(),
        category: "ROLE",
        permissions: permissions,
        db: false,
        delete: false,
        mode: mode
      };

      // Use the mutation to create/update the role
      updateRoleList.mutate([roleData]);
    }
  };

  const handleClose = () => {
    setRoleName("");
    setSelectedPermissions([]);
    setOriginalPermissions([]);
    onClose();
  };

  const getTitle = () => {
    return mode === "edit" ? t("role.edit-role") : t("role.create-new-role");
  };

  const getSubmitButtonText = () => {
    return mode === "edit" ? t("common.update-button") : t("role.add-role");
  };

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`relative w-[35rem] min-w-[35rem] ${colors.bgModal} border ${colors.borderPrimary} transform overflow-hidden rounded-lg shadow-xl transition-all duration-200`}
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2" data-testid={`${dataTestId}-header`}>
          <h2 className={`text-xl font-bold ${colors.textPrimary} uppercase`} data-testid={`${dataTestId}-title`}>
            {getTitle()}
          </h2>
        </div>

        {/* Content */}
        <div className={`px-6 pb-6 overflow-y-auto`} data-testid={`${dataTestId}-content`}>
          {/* Description */}
          <p className={`mb-8 text-sm ${colors.textSecondary}`} data-testid={`${dataTestId}-description`}>
            {t("role.create-role-description")}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Name Field */}
            <div data-testid={fieldContainerTestId("role-name")}>
              <label className={`mb-2 block max-w-max text-sm font-medium ${colors.textPrimary}`} data-testid={fieldLabelTestId("role-name")}>
                {t("role.role-name")} <span className={colors.textDanger}>*</span>
              </label>
              <CustomInput
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder={t("role.enter-role-name")}
                size="md"
                required
                maxLength={CHARACTER_LENGTH.TITLE}
                data-testid={fieldInputTestId("role-name")}
              />
            </div>

            {/* Select Permission Section */}
            <div data-testid={fieldContainerTestId("permissions")}>
              <label className={`mb-3 block text-sm font-medium ${colors.textPrimary}`} data-testid={fieldLabelTestId("permissions")}>
                {t("role.select-permission")}
              </label>
              <div className="space-y-4">
                {permissionOptions.map((permission) => (
                  <div
                    key={permission.codeSeq}
                    className="flex items-center"
                    data-testid={fieldContainerTestId(`permission-${permission.code}`)}
                  >
                    <CustomCheckbox
                      id={permission.codeSeq}
                      checked={selectedPermissions.includes(permission.code)}
                      onChange={() => handlePermissionChange(permission.code)}
                      label={permission.label}
                      size="md"
                      data-testid={fieldInputTestId(`permission-${permission.code}`)}
                    />
                    <PermissionInfoPopup
                      permissionCode={permission.code}
                      permissionLabel={permission.label}
                      data-testid={`${dataTestId}-permission-${permission.code}-info`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex justify-end gap-3 pt-4 mt-auto border-t ${colors.borderPrimary} -mx-6 px-6`} data-testid={`${dataTestId}-footer`}>
              <OutlineButton
                data-testid={`${dataTestId}-cancel-button`}
                onClick={handleClose}
                size="md"
              >
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid={`${dataTestId}-confirm-button`}
                type="submit"
                disabled={!roleName.trim() || isSubmitting}
                size="md"
              >
                {getSubmitButtonText()}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModifyRoleModal; 